import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { normalizeLocation, type ImportMetaLocation } from "@-/dev.improve.haniverse.lib.logger";
import mapObject from "@-/com.npmjs.map-obj";
import memoize from "@-/com.npmjs.memoize";
import { createActorTableQueries } from "#create-actor-table-queries";
import type { ActorQueries, ActorQueryTable, ActorTableQueries } from "#types";

// Shim so each call returns the precise per-table queries; without it the widened
// `ActorQueryTable` collapses the factory's config params to `never`.
/* eslint-disable typescript/no-unsafe-type-assertion -- intentional signature shim */
const createActorQueriesForTable = createActorTableQueries as <TTable extends ActorQueryTable>(
  location: ImportMetaLocation,
  table: TTable,
) => ActorTableQueries<TTable>;
/* eslint-enable typescript/no-unsafe-type-assertion -- Restore checked assertions after this documented bridge. */

/**
 * The per-table actor query map, bound to the caller's `import.meta.location`
 * so query logs are attributed to the read's origin. Call as
 * `actorQueries(import.meta.location).<table>.<findFirst|findMany>.…`; pass
 * `null` to opt out of logging (tests).
 *
 * Memoized on the location: `import.meta.location` is a fresh object literal per
 * evaluation, so the map is rebuilt once per distinct callsite rather than every
 * render.
 */
export const actorQueries: ActorQueries = memoize(
  (location: ImportMetaLocation) =>
    // `mapObject` yields a uniform value type; the built map is a per-table mapped type.
    /* eslint-disable typescript/no-unsafe-type-assertion -- per-table mapped shape */
    mapObject(actorSchema, (table) => [
      table,
      createActorQueriesForTable(location, table),
    ]) as unknown as ReturnType<ActorQueries>,
  /* eslint-enable typescript/no-unsafe-type-assertion -- Restore checked assertions after this documented bridge. */ {
    cacheKey: ([location]) => normalizeLocation(location),
  },
);
